import { useQuery } from "@tanstack/react-query";
import type { Schools } from "@local-logic/client";
import { useLocalLogic } from "~/context/LocalLogicProvider";

import { schoolsKeys, schoolsResponse } from "~/features/schools";

export type SchoolsParams = {
  lat?: number;
  lng?: number;
  geogId?: string;
  limit?: number;
  // Include is used to specify whether the query should run or not
  // And also to make this hoook look like the usePois hook which has a similar structure
  include?: (keyof Schools)[];
  radius?: number;
  buffer?: number;
  attemptMinimum?: number;
  includeCatchments?: boolean;
};

export function useSchools({
  lat,
  lng,
  geogId,
  include,
  radius = 2000,
  buffer = 0,
  // 30 is the default limit and maximum number of schools returned by the API
  limit = 30,
  attemptMinimum,
  includeCatchments = false,
}: SchoolsParams) {
  const { client } = useLocalLogic();

  const options: Omit<SchoolsParams, "includeCatchments"> & {
    include_catchments?: boolean;
  } = {
    include,
    limit,
    attemptMinimum,
    include_catchments: includeCatchments,
  };

  if (geogId) {
    options.geogId = geogId;
    options.buffer = buffer;
  } else {
    options.lat = lat;
    options.lng = lng;
    options.radius = radius;
  }

  return useQuery({
    queryKey: schoolsKeys.list(options),
    queryFn: async () =>
      client.getSchools(options as unknown as schoolsResponse),
    staleTime: Infinity,
    enabled:
      !!options &&
      !!options.include?.length &&
      (!!geogId || (!!options.lat && !!options.lng)),
  });
}
